import { observable, computed, action } from 'mobx';
import { computedFn } from 'mobx-utils';
import {
  RootStore, EDITOR_SHOTS_STORE, EDITOR_GROUPS_STORE, ALL_LOCATIONS, UNLOCATED_SHOTS
} from 'store';
import { Location } from './types';

export const EDITOR_LOCATIONS_STORE = 'EDITOR_LOCATIONS_STORE';

export class EditorLocationsStore {
  @observable locations: { [key: string]: Location } = {};
  @observable selectedLocation: string = null;

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  @computed get locationNames() {
    return Object.keys(this.locations);
  }

  @computed get locationsForShotsTab() {
    const locationsForShotsTab = Object.values(this.locations)
      .map(l => {
        const location = {
          ...l,
          intervals: [],
          shotsCount: 0
        };
        const shots = location.shotIds
          .map(this.rootStore[EDITOR_SHOTS_STORE].getShotById)
          .sort((a, b) => a.startFrame - b.startFrame);
        location.shotsCount = shots.length;

        let startFrame = null;
        let endFrame = null;
        shots.forEach((shot, index) => {
          const isLastIndex = index === shots.length - 1;

          switch (true) {
            case !startFrame:
              startFrame = shot.startFrame;
              if (isLastIndex) {
                endFrame = shot.endFrame;
              }
              break;

            case isLastIndex:
              endFrame = shot.endFrame;
              break;

            case shot.endFrame + 1 !== shots[index + 1].startFrame:
              endFrame = shot.endFrame;
              break;
          }

          if (startFrame && endFrame) {
            location.intervals.push({ startFrame, endFrame });
            startFrame = null;
            endFrame = null;
          }
        });

        return location;
      })
      .sort((a, b) => a.previewFrame - b.previewFrame);

    return locationsForShotsTab;
  }

  @computed get shotIdsForSelectedLocation() {
    if (!this.selectedLocation || !this.locations[this.selectedLocation]) return [];
    return this.locations[this.selectedLocation].shotIds || [];
  }

  @observable getLocationByName = computedFn(location => {
    if (this.locations[location]) {
      return { ...this.locations[location], name: location };
    }

    return {
      shotIds: [],
      previewFrame: null,
      name: location
    };
  });

  @action.bound clearEditorLocationState() {
    this.setLocations();
    this.setSelectedLocation();
  }

  @action.bound setLocations(catalog = {}) {
    this.locations = catalog;
  }

  @action.bound setSelectedLocation(value = null) {
    this.selectedLocation = value;
  }

  @action.bound selectLocation(value) {
    if (!this.locationNames.includes(value) && value !== ALL_LOCATIONS) return;
    this.setSelectedLocation(value);
    this.rootStore[EDITOR_GROUPS_STORE].setSelectedGroups();
    this.rootStore[EDITOR_SHOTS_STORE].setSelectedShotIdsForLocationChange();
  }

  @action.bound removeShotFromLocation(locationName, shotId) {
    const location = this.getLocationByName(locationName);

    location.shotIds = location.shotIds.filter(id => id.toString() !== shotId.toString());

    if (location.shotIds.length) {
      const shotStartFrames = location.shotIds
        .map(id => this.rootStore[EDITOR_SHOTS_STORE].getShotById(id).startFrame);
      location.previewFrame = Math.min(...shotStartFrames);
      this.locations[locationName] = location;
    } else {
      delete this.locations[locationName];
    }
  }

  @action.bound addShotToLocation(shot) {
    const {
      id: shotId,
      location: shotLocation,
      startFrame
    } = shot;
    const locationName = shotLocation || UNLOCATED_SHOTS;
    const location = this.getLocationByName(locationName);
    location.shotIds.push(shotId);
    location.previewFrame = location.previewFrame
      ? Math.min(location.previewFrame, startFrame)
      : startFrame;
    this.locations[locationName] = location;
  }
}
